export interface WebSocketOptions<T = any> {
    protocols?: string | string[];
    timeout?: number;
    maxAttempts?: number;
    onOpen?: (event: Event) => any;
    onMessage?: (event: MessageEvent<T>) => any;
    onReconnect?: (event: Event | CloseEvent) => any;
    onMaximum?: (event: Event | CloseEvent) => any;
    onClose?: (event: CloseEvent) => any;
    onError?: (event: Event) => any;
}

export class WsHandler<T = any> {
    private url: string;
    private options: WebSocketOptions<T>;
    private webSocket: WebSocket;
    private attempts = 0;
    private timer = 0;
    private pending: any[] = [];

    constructor(url: string, options: WebSocketOptions<T> = {}) {
        this.url = url;
        this.options = options;
        this.webSocket = this.open();
    }

    private open(): WebSocket {
        const { protocols, onClose, onError, onMessage, onOpen } = this.options;

        const webSocket = new WebSocket(this.url, protocols);

        webSocket.onmessage = onMessage || null;
        webSocket.onopen = (event) => {
            onOpen?.(event);
            this.attempts = 0;

            while (this.pending.length > 0) {
                this.send(this.pending.pop());
            }
        };
        webSocket.onclose = (event) => {
            // http://tools.ietf.org/html/rfc6455#section-7.4.1
            if (event.code === 1000 || event.code === 1001 || event.code === 1005) {
                this.reconnect(event);
            }

            onClose?.(event);
        };
        webSocket.onerror = (event) => {
            // @ts-ignore: missing code
            if (event.code === 'ECONNREFUSED') {
                this.reconnect(event);
            } else {
                onError?.(event);
            }
        };

        return webSocket;
    }

    reconnect(event: Event | CloseEvent) {
        const { maxAttempts = 0, timeout, onReconnect, onMaximum } = this.options;

        if (this.attempts++ < maxAttempts) {
            this.timer = window.setTimeout(() => {
                onReconnect?.(event);
                this.webSocket = this.open();
            }, timeout || 1000);
        } else {
            onMaximum?.(event);
        }
    }

    json(payload: any) {
        this.send(JSON.stringify(payload));
    }

    send(payload: any) {
        if (this.isOpen) {
            this.webSocket.send(payload);
        } else {
            this.pending.push(payload);
        }
    }

    close(code?: number, reason?: string) {
        window.clearTimeout(this.timer);
        this.webSocket.close(code || 1e3, reason);
    }

    updateUrl(url: string) {
        this.url = url;
    }

    getWebSocket(): WebSocket {
        return this.webSocket;
    }

    get state() {
        return this.webSocket.readyState;
    }

    get isOpen() {
        return this.state === WebSocket.OPEN;
    }
}
